// src/components/dashboards/SampleCollector.jsx
// Role: SAMPLE_COLLECTOR — handles sample collection for ALL lab categories
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../shared/DashboardLayout';
import { labAPI } from '../../services/api';

// ─── Design Tokens ────────────────────────────────────────────────────────────
const C = {
  white:      '#ffffff',
  bg:         '#f7f8fc',
  bgAlt:      '#f0f2f8',
  surface:    '#ffffff',
  border:     '#e4e7ef',
  borderMid:  '#d0d5e8',
  ink:        '#111827',
  inkMid:     '#374151',
  inkSoft:    '#6b7280',
  inkGhost:   '#9ca3af',
  primary:    '#2563eb',
  primaryBg:  '#eff6ff',
  primaryBdr: '#bfdbfe',
  green:      '#059669',
  greenBg:    '#ecfdf5',
  greenBdr:   '#a7f3d0',
  red:        '#dc2626',
  redBg:      '#fef2f2',
  redBdr:     '#fecaca',
  orange:     '#d97706',
  orangeBg:   '#fffbeb',
  orangeBdr:  '#fde68a',
  violet:     '#7c3aed',
  violetBg:   '#f5f3ff',
  violetBdr:  '#ddd6fe',
  teal:       '#0891b2',
  tealBg:     '#ecfeff',
  tealBdr:    '#a5f3fc',
  amber:      '#b45309',
  amberBg:    '#fffbeb',
  amberBdr:   '#fde68a',
  mono:       "'Roboto Mono', 'Courier New', monospace",
};

const CAT = {
  Microbiology: { color: C.amber,  bg: C.amberBg,  bdr: C.amberBdr,  icon: '🦠', abbr: 'MICRO' },
  Pathology:    { color: C.violet, bg: C.violetBg, bdr: C.violetBdr, icon: '🔬', abbr: 'PATH'  },
  Biochemistry: { color: C.teal,   bg: C.tealBg,   bdr: C.tealBdr,   icon: '⚗️', abbr: 'BIO'   },
};

const URGENCY = {
  STAT:    { label: 'STAT',    color: C.red,    bg: C.redBg,    bdr: C.redBdr    },
  URGENT:  { label: 'URGENT',  color: C.orange, bg: C.orangeBg, bdr: C.orangeBdr },
  ROUTINE: { label: 'ROUTINE', color: C.green,  bg: C.greenBg,  bdr: C.greenBdr  },
};

// ─── Inject Styles ────────────────────────────────────────────────────────────
const injectStyles = () => {
  if (document.getElementById('sc2-styles')) return;
  const s = document.createElement('style');
  s.id = 'sc2-styles';
  s.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Roboto+Mono:wght@400;500;700&display=swap');

    .sc2 * { box-sizing: border-box; }
    .sc2, .sc2 body { font-family: 'Plus Jakarta Sans', sans-serif; }

    .sc2-input {
      width: 100%; padding: 10px 14px;
      border: 1.5px solid ${C.border}; border-radius: 10px;
      font-size: 14px; font-family: 'Plus Jakarta Sans', sans-serif;
      color: ${C.ink}; background: ${C.white}; outline: none;
      transition: border-color .18s, box-shadow .18s;
    }
    .sc2-input:focus { border-color: ${C.primary}; box-shadow: 0 0 0 3px ${C.primaryBg}; }
    .sc2-input::placeholder { color: ${C.inkGhost}; }

    .sc2-btn {
      padding: 10px 20px; border: none; border-radius: 10px;
      font-size: 13px; font-weight: 700; font-family: 'Plus Jakarta Sans', sans-serif;
      cursor: pointer; display: inline-flex; align-items: center;
      gap: 7px; transition: all .18s; white-space: nowrap;
    }
    .sc2-btn:disabled { opacity: .5; cursor: not-allowed; }
    .sc2-btn-primary {
      background: ${C.primary}; color: #fff;
      box-shadow: 0 2px 8px rgba(37,99,235,.25);
    }
    .sc2-btn-primary:hover:not(:disabled) {
      background: #1d4ed8; transform: translateY(-1px);
      box-shadow: 0 4px 14px rgba(37,99,235,.35);
    }
    .sc2-btn-green {
      background: ${C.green}; color: #fff;
      box-shadow: 0 2px 8px rgba(5,150,105,.25);
    }
    .sc2-btn-green:hover:not(:disabled) {
      background: #047857; transform: translateY(-1px);
    }
    .sc2-btn-outline {
      background: ${C.white}; border: 1.5px solid ${C.border}; color: ${C.inkMid};
    }
    .sc2-btn-outline:hover { border-color: ${C.borderMid}; background: ${C.bg}; }

    .sc2-pill {
      padding: 5px 14px; border-radius: 99px; font-size: 12px; font-weight: 700;
      font-family: 'Plus Jakarta Sans', sans-serif; cursor: pointer;
      border: 1.5px solid; transition: all .18s;
    }

    .sc2-row { transition: background .12s; }
    .sc2-row:hover td { background: ${C.bg} !important; }

    .sc2-modal-overlay {
      position: fixed; inset: 0; background: rgba(17,24,39,.45);
      display: flex; align-items: center; justify-content: center;
      z-index: 1000; backdrop-filter: blur(5px);
      animation: sc2Fade .15s ease;
    }
    .sc2-modal {
      background: ${C.white}; border-radius: 18px;
      box-shadow: 0 24px 80px rgba(17,24,39,.15), 0 0 0 1px ${C.border};
      animation: sc2Rise .2s ease;
    }

    .sc2-stype-btn {
      padding: 8px 6px; border-radius: 8px; font-size: 12px; font-weight: 700;
      font-family: 'Plus Jakarta Sans', sans-serif; cursor: pointer;
      border: 1.5px solid; transition: all .15s; text-align: center;
    }

    @keyframes sc2Fade { from { opacity: 0 } to { opacity: 1 } }
    @keyframes sc2Rise { from { opacity: 0; transform: translateY(14px) } to { opacity: 1; transform: translateY(0) } }

    ::-webkit-scrollbar { width: 5px; height: 5px; }
    ::-webkit-scrollbar-track { background: transparent; }
    ::-webkit-scrollbar-thumb { background: ${C.borderMid}; border-radius: 99px; }
  `;
  document.head.appendChild(s);
};

// ─── Barcode Visual ───────────────────────────────────────────────────────────
const Barcode = ({ code }) => {
  const data = code.split('').map(c => c.charCodeAt(0));
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ background: C.white, border: `1.5px solid ${C.border}`,
        borderRadius: 10, padding: '12px 18px', display: 'inline-block' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '1px',
          justifyContent: 'center', height: 46, marginBottom: 8 }}>
          {data.map((b, i) => (
            <div key={i} style={{
              width: b % 3 === 0 ? 3 : b % 3 === 1 ? 2 : 1,
              height: b % 4 === 0 ? 46 : b % 4 === 1 ? 36 : b % 4 === 2 ? 42 : 30,
              background: C.ink, borderRadius: 1,
            }} />
          ))}
        </div>
        <div style={{ fontFamily: C.mono, fontSize: 12, fontWeight: 700,
          letterSpacing: 3, color: C.inkMid }}>{code}</div>
      </div>
    </div>
  );
};

const printLabel = (order) => {
  const { sample } = order;
  const bars = sample.barcode.split('').map(c => c.charCodeAt(0));
  const barsHtml = bars.map(b =>
    `<div style="width:${b%3===0?3:b%3===1?2:1}px;height:${b%4===0?44:b%4===1?34:b%4===2?40:28}px;background:#111827;display:inline-block;margin:0 .5px;vertical-align:bottom;border-radius:1px;"></div>`
  ).join('');
  const cats = [...new Set((order.items || []).map(i => i.test?.category_name || '').filter(Boolean))];
  const win = window.open('', '_blank', 'width=440,height=330');
  win.document.write(`<html><head><title>Label</title>
    <style>
      @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@600;700;800&family=Roboto+Mono:wght@700&display=swap');
      body { font-family:'Plus Jakarta Sans',sans-serif; background:#f7f8fc; margin:0; padding:20px; }
      .card { background:#fff; border:1.5px solid #e4e7ef; border-radius:14px; padding:20px;
        display:inline-block; min-width:330px; max-width:390px; }
      .header { font-size:10px; font-weight:800; letter-spacing:.1em; text-transform:uppercase;
        color:#9ca3af; margin-bottom:12px; }
      .name { font-size:18px; font-weight:800; color:#111827; margin-bottom:3px; }
      .meta { font-size:12px; color:#6b7280; margin-bottom:14px; }
      .bcode { background:#f7f8fc; border:1.5px solid #e4e7ef; border-radius:10px;
        padding:12px; text-align:center; margin-bottom:12px; }
      .mono { font-family:'Roboto Mono',monospace; font-size:13px; font-weight:700;
        letter-spacing:3px; color:#374151; margin-top:8px; }
      .tag { display:inline-block; padding:3px 10px; border-radius:99px; margin:2px;
        font-size:10px; font-weight:700; background:#eff6ff; color:#2563eb; border:1px solid #bfdbfe; }
      .tests { font-size:11px; color:#6b7280; margin-top:8px; border-top:1px dashed #e4e7ef; padding-top:8px; }
      @media print { body { background:white; } }
    </style></head><body>
    <div class="card">
      <div class="header">🏥 Lab Sample Collection Label</div>
      <div class="name">${order.patient_name || '—'}</div>
      <div class="meta">
        Patient ID: <strong>${order.patient_id || '—'}</strong> &nbsp;·&nbsp;
        Token: <strong>${order.token_number || '—'}</strong>
        ${order.urgency && order.urgency !== 'ROUTINE' ? `&nbsp;·&nbsp; <strong style="color:#dc2626;">${order.urgency}</strong>` : ''}
      </div>
      <div class="bcode">
        <div style="display:flex;align-items:flex-end;justify-content:center;gap:1px;">${barsHtml}</div>
        <div class="mono">${sample.barcode}</div>
      </div>
      <div>${cats.map(c => `<span class="tag">${c}</span>`).join('')}</div>
      <div class="tests">
        ${(order.items || []).map(i => i.test_name).join(' · ')}
        ${sample.sample_type ? `<br><strong>Sample:</strong> ${sample.sample_type}${sample.quantity ? ' — ' + sample.quantity : ''}` : ''}
      </div>
    </div>
    <script>window.onload=()=>{window.print();window.close()}<\/script>
    </body></html>`);
  win.document.close();
};

// ─── Stat Card ────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, sub, color = C.primary, icon }) => (
  <div style={{
    background: C.white, borderRadius: 14, padding: '20px 22px',
    border: `1.5px solid ${C.border}`,
    borderTop: `3px solid ${color}`,
    boxShadow: '0 1px 4px rgba(17,24,39,.05)',
  }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, color: C.inkGhost,
          textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 10 }}>{label}</div>
        <div style={{ fontSize: 30, fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
        {sub && <div style={{ fontSize: 12, color: C.inkSoft, marginTop: 6 }}>{sub}</div>}
      </div>
      <div style={{ fontSize: 28, opacity: .75 }}>{icon}</div>
    </div>
  </div>
);

// ─── Collection Modal ─────────────────────────────────────────────────────────
const CollectionModal = ({ order, onClose, onSuccess }) => {
  const defaultType = order.items?.[0]?.sample_type || '';
  const [form, setForm] = useState({
    sample_type: defaultType,
    quantity: '',
    notes: '',
    barcode: `LAB${String(order.id).padStart(5, '0')}${Date.now().toString().slice(-5)}`,
  });
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const cats = [...new Set((order.items || []).map(i => i.test?.category_name || '').filter(Boolean))];
  const urgMeta = URGENCY[order.urgency] || URGENCY.ROUTINE;

  const handleSubmit = async () => {
    if (!form.sample_type.trim()) { setError('Sample type is required'); return; }
    if (!form.barcode.trim())     { setError('Barcode is required'); return; }
    setLoading(true); setError('');
    try {
      const result = await labAPI.collectSample(order.id, form);
      onSuccess(result);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to record sample collection');
    } finally { setLoading(false); }
  };

  const SAMPLE_TYPES = ['Blood', 'Urine', 'Stool', 'Sputum', 'Swab', 'CSF', 'Tissue', 'Other'];

  return (
    <div className="sc2-modal-overlay" onClick={onClose}>
      <div className="sc2-modal" style={{ width: 540 }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: '22px 26px', borderBottom: `1.5px solid ${C.border}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: C.ink, display: 'flex',
              alignItems: 'center', gap: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: C.primaryBg,
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>💉</div>
              Record Sample Collection
            </div>
            <div style={{ fontSize: 12, color: C.inkSoft, marginTop: 6, marginLeft: 46 }}>
              Order #{order.id}
              {order.urgency !== 'ROUTINE' && (
                <span style={{ marginLeft: 8, padding: '2px 9px', borderRadius: 99,
                  background: urgMeta.bg, color: urgMeta.color, border: `1px solid ${urgMeta.bdr}`,
                  fontSize: 11, fontWeight: 700 }}>{urgMeta.label}</span>
              )}
            </div>
          </div>
          <button onClick={onClose} style={{ background: C.bg, border: `1.5px solid ${C.border}`,
            width: 32, height: 32, borderRadius: 8, cursor: 'pointer',
            color: C.inkSoft, fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
        </div>

        <div style={{ padding: '22px 26px', maxHeight: '70vh', overflowY: 'auto' }}>

          {/* Patient card */}
          <div style={{ background: C.bg, border: `1.5px solid ${C.border}`,
            borderRadius: 12, padding: '14px 18px', marginBottom: 20 }}>
            <div style={{ fontWeight: 800, fontSize: 15, color: C.ink }}>{order.patient_name}</div>
            <div style={{ fontSize: 12, color: C.inkSoft, marginTop: 4, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              <span>ID: <span style={{ fontFamily: C.mono, fontWeight: 700, color: C.inkMid }}>{order.patient_id}</span></span>
              <span>Token: <span style={{ fontFamily: C.mono, fontWeight: 700, color: C.primary }}>{order.token_number || '—'}</span></span>
              {order.doctor_name && <span>Dr. {order.doctor_name}</span>}
            </div>
            {cats.length > 0 && (
              <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                {cats.map(cat => {
                  const cs = CAT[cat] || {};
                  return (
                    <span key={cat} style={{ padding: '3px 10px', borderRadius: 99, fontSize: 11,
                      fontWeight: 700, background: cs.bg, color: cs.color, border: `1px solid ${cs.bdr}` }}>
                      {cs.icon} {cs.abbr}
                    </span>
                  );
                })}
                {(order.items || []).map((item, i) => (
                  <span key={i} style={{ fontSize: 12, color: C.inkSoft }}>
                    {i === 0 ? '' : '· '}{item.test_name}
                  </span>
                ))}
              </div>
            )}
          </div>

          {error && (
            <div style={{ background: C.redBg, border: `1.5px solid ${C.redBdr}`, color: C.red,
              padding: '10px 14px', borderRadius: 10, marginBottom: 16, fontSize: 13, fontWeight: 600 }}>
              ⚠ {error}
            </div>
          )}

          <div style={{ display: 'grid', gap: 18 }}>
            {/* Barcode */}
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: C.inkSoft, display: 'block',
                marginBottom: 7, textTransform: 'uppercase', letterSpacing: '.06em' }}>Barcode *</label>
              <input className="sc2-input" style={{ fontFamily: C.mono, fontWeight: 700, letterSpacing: 2 }}
                value={form.barcode} onChange={e => setForm(p => ({ ...p, barcode: e.target.value }))} />
            </div>

            {/* Sample type buttons */}
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: C.inkSoft, display: 'block',
                marginBottom: 7, textTransform: 'uppercase', letterSpacing: '.06em' }}>Sample Type *</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 7, marginBottom: 10 }}>
                {SAMPLE_TYPES.map(st => {
                  const sel = form.sample_type === st;
                  return (
                    <button key={st} type="button" className="sc2-stype-btn"
                      style={{
                        background: sel ? C.primaryBg : C.white,
                        color: sel ? C.primary : C.inkSoft,
                        borderColor: sel ? C.primary : C.border,
                      }}
                      onClick={() => setForm(p => ({ ...p, sample_type: st }))}>
                      {st}
                    </button>
                  );
                })}
              </div>
              <input className="sc2-input" placeholder="Or type a custom sample type…"
                value={form.sample_type}
                onChange={e => setForm(p => ({ ...p, sample_type: e.target.value }))} />
            </div>

            {/* Volume + Time */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: C.inkSoft, display: 'block',
                  marginBottom: 7, textTransform: 'uppercase', letterSpacing: '.06em' }}>Volume / Qty</label>
                <input className="sc2-input" placeholder="e.g. 5 mL"
                  value={form.quantity} onChange={e => setForm(p => ({ ...p, quantity: e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 700, color: C.inkSoft, display: 'block',
                  marginBottom: 7, textTransform: 'uppercase', letterSpacing: '.06em' }}>Collection Time</label>
                <input className="sc2-input" readOnly
                  value={new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                  style={{ background: C.bg, color: C.inkSoft, cursor: 'default' }} />
              </div>
            </div>

            {/* Notes */}
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: C.inkSoft, display: 'block',
                marginBottom: 7, textTransform: 'uppercase', letterSpacing: '.06em' }}>Collection Notes</label>
              <textarea className="sc2-input" style={{ minHeight: 72, resize: 'vertical' }}
                placeholder="Haemolysed sample, difficult venipuncture, patient fasting status…"
                value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} />
            </div>
          </div>
        </div>

        <div style={{ padding: '18px 26px', borderTop: `1.5px solid ${C.border}`,
          display: 'flex', gap: 10, justifyContent: 'flex-end', background: C.bg,
          borderRadius: '0 0 18px 18px' }}>
          <button className="sc2-btn sc2-btn-outline" onClick={onClose}>Cancel</button>
          <button className="sc2-btn sc2-btn-green" onClick={handleSubmit} disabled={loading}>
            {loading ? '⏳ Saving…' : '✓ Confirm Collection & Print Label'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Success Modal ────────────────────────────────────────────────────────────
const SuccessModal = ({ order, onClose }) => (
  <div className="sc2-modal-overlay" onClick={onClose}>
    <div className="sc2-modal" style={{ width: 440 }} onClick={e => e.stopPropagation()}>
      <div style={{ padding: '22px 26px', borderBottom: `1.5px solid ${C.border}`,
        display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{ width: 44, height: 44, borderRadius: 12, background: C.greenBg,
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>✅</div>
        <div>
          <div style={{ fontSize: 17, fontWeight: 800, color: C.ink }}>Sample Collected</div>
          <div style={{ fontSize: 13, color: C.inkSoft, marginTop: 2 }}>
            {order.patient_name} · Token <span style={{ fontFamily: C.mono, fontWeight: 700,
              color: C.primary }}>{order.token_number}</span>
          </div>
        </div>
      </div>
      <div style={{ padding: '24px 26px' }}>
        <Barcode code={order.sample.barcode} />
        <div style={{ marginTop: 14, fontSize: 13, color: C.inkSoft, textAlign: 'center' }}>
          Sample: <strong style={{ color: C.inkMid }}>{order.sample.sample_type}</strong>
          {order.sample.quantity && <> · Volume: <strong style={{ color: C.inkMid }}>{order.sample.quantity}</strong></>}
        </div>
      </div>
      <div style={{ padding: '16px 26px', borderTop: `1.5px solid ${C.border}`,
        display: 'flex', gap: 10, justifyContent: 'center', background: C.bg,
        borderRadius: '0 0 18px 18px' }}>
        <button className="sc2-btn sc2-btn-primary" onClick={() => printLabel(order)}>🖨 Print Label</button>
        <button className="sc2-btn sc2-btn-outline" onClick={onClose}>Close</button>
      </div>
    </div>
  </div>
);

// ─── Table styles ─────────────────────────────────────────────────────────────
const thS = {
  padding: '11px 18px', textAlign: 'left',
  background: C.bg, color: C.inkGhost, fontWeight: 700,
  fontSize: 11, textTransform: 'uppercase', letterSpacing: '.07em',
  borderBottom: `1.5px solid ${C.border}`,
  fontFamily: "'Plus Jakarta Sans', sans-serif",
};
const tdS = {
  padding: '14px 18px', borderBottom: `1px solid ${C.border}`,
  color: C.ink, verticalAlign: 'middle', fontSize: 13,
  fontFamily: "'Plus Jakarta Sans', sans-serif",
};

// ─── Main Component ───────────────────────────────────────────────────────────
const SampleCollector = () => {
  const { user } = useAuth();
  const [orders,       setOrders]       = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [searchQ,      setSearchQ]      = useState('');
  const [catFilter,    setCatFilter]    = useState('all');
  const [collectModal, setCollectModal] = useState(null);
  const [successModal, setSuccessModal] = useState(null);

  useEffect(() => { injectStyles(); }, []);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const data = await labAPI.getOrders({ status: 'PAID' });
      setOrders(Array.isArray(data) ? data : data.results || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const allCategories = [...new Set(
    orders.flatMap(o => (o.items || []).map(i => i.test?.category_name || '').filter(Boolean))
  )].sort();

  const filtered = orders.filter(o => {
    if (catFilter !== 'all') {
      if (!(o.items || []).some(i => (i.test?.category_name || '') === catFilter)) return false;
    }
    if (!searchQ) return true;
    const q = searchQ.toLowerCase();
    return (
      o.patient_name?.toLowerCase().includes(q) ||
      o.patient_id?.toLowerCase().includes(q)   ||
      o.token_number?.toLowerCase().includes(q)
    );
  });

  const urgRank = { STAT: 0, URGENT: 1, ROUTINE: 2 };
  const sorted = [...filtered].sort((a, b) => (urgRank[a.urgency] ?? 2) - (urgRank[b.urgency] ?? 2));

  const handleCollected = (updated) => {
    setCollectModal(null);
    setSuccessModal(updated);
    fetchOrders();
  };

  const statCounts = {
    total:   orders.length,
    stat:    orders.filter(o => o.urgency === 'STAT').length,
    urgent:  orders.filter(o => o.urgency === 'URGENT').length,
    routine: orders.filter(o => o.urgency === 'ROUTINE').length,
  };

  return (
    <DashboardLayout title="Sample Collector">
      <div className="sc2" style={{ minHeight: '100vh', background: C.bg, padding: '28px 32px' }}>

        {/* ── Header ── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ width: 50, height: 50, borderRadius: 14, background: C.primaryBg,
              border: `2px solid ${C.primaryBdr}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>
              💉
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: C.ink, lineHeight: 1.2 }}>
                Sample Collection
              </h1>
              <p style={{ margin: '5px 0 0', fontSize: 13, color: C.inkSoft }}>
                All laboratory categories &nbsp;·&nbsp;
                <strong style={{ color: C.primary }}>{user?.first_name || user?.username}</strong>
                &nbsp;·&nbsp; {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
            </div>
          </div>
        </div>

        {/* ── Stats ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
          <StatCard label="Pending Collection" value={statCounts.total}
            sub="PAID · awaiting sample" color={C.primary} icon="📋" />
          <StatCard label="STAT" value={statCounts.stat}
            sub="Immediate priority" color={C.red} icon="🔴" />
          <StatCard label="Urgent" value={statCounts.urgent}
            sub="High priority" color={C.orange} icon="⚡" />
          <StatCard label="Routine" value={statCounts.routine}
            sub="Standard priority" color={C.green} icon="🟢" />
        </div>

        {/* ── Category Filter Pills ── */}
        {allCategories.length > 0 && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: C.inkGhost,
              textTransform: 'uppercase', letterSpacing: '.06em', marginRight: 4 }}>Filter:</span>
            <button className="sc2-pill" style={{
              background: catFilter === 'all' ? C.ink : C.white,
              color: catFilter === 'all' ? C.white : C.inkSoft,
              borderColor: catFilter === 'all' ? C.ink : C.border,
            }} onClick={() => setCatFilter('all')}>All ({orders.length})</button>
            {allCategories.map(cat => {
              const cs = CAT[cat] || { color: C.primary, bg: C.primaryBg, bdr: C.primaryBdr, icon: '🧪', abbr: cat };
              const cnt = orders.filter(o => (o.items || []).some(i => (i.test?.category_name || '') === cat)).length;
              const active = catFilter === cat;
              return (
                <button key={cat} className="sc2-pill" style={{
                  background: active ? cs.color : cs.bg,
                  color: active ? C.white : cs.color,
                  borderColor: active ? cs.color : cs.bdr,
                }} onClick={() => setCatFilter(active ? 'all' : cat)}>
                  {cs.icon} {cat} ({cnt})
                </button>
              );
            })}
          </div>
        )}

        {/* ── Main Table ── */}
        <div style={{ background: C.white, borderRadius: 16, border: `1.5px solid ${C.border}`,
          boxShadow: '0 2px 16px rgba(17,24,39,.06)', overflow: 'hidden' }}>

          {/* Table header bar */}
          <div style={{ padding: '16px 22px', borderBottom: `1.5px solid ${C.border}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: C.white }}>
            <div>
              <span style={{ fontSize: 15, fontWeight: 800, color: C.ink }}>
                Awaiting Collection
              </span>
              <span style={{ marginLeft: 10, fontSize: 13, color: C.inkGhost }}>
                {sorted.length} {catFilter !== 'all' ? `in ${catFilter}` : 'orders'}
              </span>
            </div>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)',
                fontSize: 14, color: C.inkGhost }}>🔍</span>
              <input className="sc2-input" style={{ width: 290, paddingLeft: 38 }}
                placeholder="Search patient name, ID, token…"
                value={searchQ} onChange={e => setSearchQ(e.target.value)} />
            </div>
          </div>

          {loading ? (
            <div style={{ padding: 64, textAlign: 'center' }}>
              <div style={{ fontSize: 38, marginBottom: 14, opacity: .4 }}>⏳</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: C.inkSoft }}>Loading orders…</div>
            </div>
          ) : sorted.length === 0 ? (
            <div style={{ padding: 64, textAlign: 'center' }}>
              <div style={{ width: 70, height: 70, borderRadius: '50%', background: C.bg,
                border: `2px solid ${C.border}`, margin: '0 auto 18px',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 30 }}>💉</div>
              <div style={{ fontSize: 17, fontWeight: 800, color: C.inkMid, marginBottom: 6 }}>
                {searchQ ? 'No results found' : 'No orders pending collection'}
              </div>
              <div style={{ fontSize: 13, color: C.inkSoft }}>
                {searchQ ? 'Try a different search term.' : 'Orders appear here once billing confirms payment.'}
              </div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['Patient', 'Token', 'Tests & Categories', 'Urgency', 'Instructions', 'Action'].map(h => (
                      <th key={h} style={thS}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sorted.map(order => {
                    const cats = [...new Set((order.items || []).map(i => i.test?.category_name || '').filter(Boolean))];
                    const urg  = URGENCY[order.urgency] || URGENCY.ROUTINE;
                    return (
                      <tr key={order.id} className="sc2-row">
                        <td style={tdS}>
                          <div style={{ fontWeight: 800, fontSize: 14, color: C.ink }}>{order.patient_name}</div>
                          <div style={{ fontFamily: C.mono, fontSize: 11, color: C.inkGhost, marginTop: 3 }}>
                            {order.patient_id}
                          </div>
                        </td>
                        <td style={tdS}>
                          <span style={{ fontFamily: C.mono, fontWeight: 700, fontSize: 14, color: C.primary }}>
                            {order.token_number || '—'}
                          </span>
                        </td>
                        <td style={{ ...tdS, maxWidth: 260 }}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 7 }}>
                            {cats.map(cat => {
                              const cs = CAT[cat] || {};
                              return (
                                <span key={cat} style={{ padding: '3px 10px', borderRadius: 99, fontSize: 11,
                                  fontWeight: 700, background: cs.bg, color: cs.color, border: `1px solid ${cs.bdr}` }}>
                                  {cs.icon} {cs.abbr}
                                </span>
                              );
                            })}
                          </div>
                          <div style={{ fontSize: 12, color: C.inkSoft, lineHeight: 1.5 }}>
                            {(order.items || []).map(i => i.test_name).join(' · ')}
                          </div>
                        </td>
                        <td style={tdS}>
                          <span style={{ padding: '5px 12px', borderRadius: 99, fontSize: 11,
                            fontWeight: 800, background: urg.bg, color: urg.color,
                            border: `1.5px solid ${urg.bdr}` }}>
                            {urg.label}
                          </span>
                        </td>
                        <td style={{ ...tdS, maxWidth: 180 }}>
                          {order.special_instructions ? (
                            <div style={{ fontSize: 12, color: C.inkSoft, fontStyle: 'italic',
                              lineHeight: 1.5, background: C.bg, padding: '6px 10px', borderRadius: 8 }}>
                              "{order.special_instructions}"
                            </div>
                          ) : (
                            <span style={{ color: C.inkGhost, fontSize: 12 }}>—</span>
                          )}
                        </td>
                        <td style={{ ...tdS, whiteSpace: 'nowrap' }}>
                          <button className="sc2-btn sc2-btn-primary"
                            style={{ padding: '8px 16px', fontSize: 12 }}
                            onClick={() => setCollectModal(order)}>
                            💉 Collect
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {collectModal && (
        <CollectionModal
          order={collectModal}
          onClose={() => setCollectModal(null)}
          onSuccess={handleCollected}
        />
      )}
      {successModal?.sample && (
        <SuccessModal
          order={successModal}
          onClose={() => setSuccessModal(null)}
        />
      )}
    </DashboardLayout>
  );
};

export default SampleCollector;